import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';
import { generateUrlByKey, useCurrentRoute } from 'components/root/router';

import LoaderOverlay from '../../../../../common/elements/Loader/LoaderOverlay';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { formatPhoneNumber } from '../../../../../services/geo/e-164';
import { NS_COMMON, NSC_PATIENT, NSPP_PATIENT } from '../../../../../services/i18n/namespaces';
import { getPatientAgeFromDob, getPatientFullName, getPatientPreferredName } from '../../../../../services/patient';
import { dateFormatForShow } from '../PatientStatusChange/PatientStatusChangeConstants';

import PatientProfileSidebarTab from './PatientProfileSidebarTab/PatientProfileSidebarTab';
import GoToPatientStudiesButton from './GoToPatientStudiesButton';
import { usePatientProfile, withPatientProfile } from './PatientProfileContext';

import './PatientProfile.scss';

function PatientProfile() {
  const { t } = useTranslation();
  const currentRoute = useCurrentRoute();
  const navigate = useNavigate();
  const { patientInfo, dictionariesMap, studyHistory, hasAccessToPatient } = usePatientProfile();
  const isPatientUnavailableToContact = patientInfo?.isDeceased || patientInfo?.phonesInfo?.neverCallAgain;
  const isDataLoaded = !isEmpty(dictionariesMap) && !isEmpty(patientInfo) && studyHistory !== null;

  useEffect(() => {
    let timeout;
    if (!isDataLoaded && hasAccessToPatient === false) {
      NotificationManager.error('You do not have access to the patient, redirecting you to the home page');
      timeout = setTimeout(() => navigate('/'), 2000);
    }

    return () => clearTimeout(timeout);
  }, [isDataLoaded, hasAccessToPatient, navigate]);

  if (!isDataLoaded) {
    return <LoaderOverlay className="eds-loader-manager" />;
  }

  return (
    <>
      <PageInfoHeader
        objectRecordLabel={getPatientFullName(patientInfo) + getPatientPreferredName(patientInfo)}
        pageInfo={
          <PageInfoHeader.CollapsibleList>
            <PageInfoHeader.AdditionalInfo title={t('patientId', { ns: NSC_PATIENT })}>
              {patientInfo?.patientId}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t('dateOfBirth', { ns: NS_COMMON })}>
              {patientInfo?.dob?.format(dateFormatForShow)} ({getPatientAgeFromDob(patientInfo)})
            </PageInfoHeader.AdditionalInfo>
            {patientInfo?.sex && (
              <PageInfoHeader.AdditionalInfo title={t('sex', { ns: NSC_PATIENT })}>
                {t(`sexMap.${dictionariesMap?.sexCodes[patientInfo?.sex]?.id}`, { ns: NSC_PATIENT })}
              </PageInfoHeader.AdditionalInfo>
            )}
            {patientInfo?.phonesInfo?.primary && (
              <PageInfoHeader.AdditionalInfo title={t('info.phoneNumber', { ns: NSPP_PATIENT })}>
                {formatPhoneNumber(patientInfo?.phonesInfo?.primary, patientInfo?.phonesInfo?.primaryPhoneCode)}
              </PageInfoHeader.AdditionalInfo>
            )}
          </PageInfoHeader.CollapsibleList>
        }
      />
      {studyHistory && !isEmpty(studyHistory) && <GoToPatientStudiesButton />}
      <div className="patient-profile">
        <div className="patient-profile-sidebar">
          <PatientProfileSidebarTab isPatientUnavailableToContact={isPatientUnavailableToContact}>
            Patient Details
          </PatientProfileSidebarTab>
          <PatientProfileSidebarTab>Medications</PatientProfileSidebarTab>
          <PatientProfileSidebarTab>Medical History</PatientProfileSidebarTab>
        </div>
        <div className="patient-profile-content">
          {currentRoute.name === 'Patient Profile' ? (
            <Navigate to={generateUrlByKey(`${currentRoute.key}.Patient Details`, currentRoute.params)} replace />
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </>
  );
}

export default withPatientProfile(PatientProfile);
